import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Star, Clock, ArrowsClockwise, ShieldCheck, Heart, ChatCircle, MapPin, CheckCircle, X } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TrustScore from "@/components/TrustScore";
import Avatar from "@/components/Avatar";

const riskInfo = {
  low: { label: "LOW RISK", desc: "Manual, technical & on-page work that follows guidelines.", cls: "risk-low" },
  medium: { label: "MEDIUM RISK", desc: "Off-site signals that require careful execution.", cls: "risk-medium" },
  high: { label: "HIGH RISK", desc: "Aggressive tactics that may carry algorithmic risk.", cls: "risk-high" },
};

function PackageCard({ p, svc, addonIdx, setAddonIdx, grand, onBuy, onContact, onSave, testIdSuffix }) {
  const isPremium = p.name === "premium";
  return (
    <div
      className={`rounded-2xl p-6 ${isPremium ? "tier-premium-card" : "bg-white border border-gray-200"}`}
      data-testid={`package-card-${testIdSuffix}`}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className={`text-xs font-bold uppercase tracking-[0.2em] ${isPremium ? "text-mv-accent" : "text-mv-primary"}`}>{p.title}</div>
          <div className={`font-display font-black text-3xl mt-1 ${isPremium ? "text-white" : "text-mv-secondary"}`}>${p.price}</div>
        </div>
        <div className={`text-xs flex items-center gap-1 ${isPremium ? "text-white/70" : "text-gray-500"}`}>
          <Clock size={14} /> {p.delivery_days} days
          <span className="mx-1">·</span>
          <ArrowsClockwise size={14} /> {p.revisions} rev
        </div>
      </div>
      {p.description && (
        <p className={`mt-3 text-sm ${isPremium ? "text-white/80" : "text-gray-600"}`} data-testid="package-description">
          {p.description}
        </p>
      )}
      <ul className={`mt-5 space-y-2 text-sm ${isPremium ? "text-white/85" : "text-gray-700"}`}>
        {p.features.map((f, i) => (
          <li key={i} className="flex gap-2">
            <CheckCircle weight="duotone" className={isPremium ? "text-mv-accent" : "text-mv-primary"} /> {f}
          </li>
        ))}
      </ul>

      {svc.addons.length > 0 && (
        <div className="mt-5">
          <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${isPremium ? "text-white/60" : "text-gray-500"}`}>Add-ons</div>
          <div className="space-y-1">
            {svc.addons.map((a, i) => (
              <label key={i} className={`flex items-center justify-between text-sm gap-2 cursor-pointer ${isPremium ? "text-white/90" : "text-gray-700"}`}>
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={addonIdx.includes(i)} onChange={(e) => setAddonIdx((cur) => e.target.checked ? [...cur, i] : cur.filter((x) => x !== i))} />
                  {a.title}
                </span>
                <span className="font-bold">+${a.price}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className={`mt-5 flex items-center justify-between border-t pt-4 ${isPremium ? "border-white/20" : "border-gray-200"}`}>
        <span className={isPremium ? "text-white/70 text-sm" : "text-gray-500 text-sm"}>Total</span>
        <span className={`font-display font-black text-2xl ${isPremium ? "text-mv-accent" : "text-mv-secondary"}`}>${grand}</span>
      </div>

      <Button onClick={onBuy} data-testid="buy-now-btn"
        className={`w-full mt-5 h-12 rounded-xl font-black ${isPremium ? "bg-mv-accent hover:bg-lime-400 text-mv-secondary" : "bg-mv-primary hover:bg-mv-primary-light text-white"}`}>
        Continue (${grand})
      </Button>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Button onClick={onContact} variant="outline" className="rounded-xl" data-testid="contact-seller-btn">
          <ChatCircle size={16} className="mr-1.5" weight="duotone" /> Message
        </Button>
        <Button onClick={onSave} variant="outline" className="rounded-xl" data-testid="save-service-btn">
          <Heart size={16} className="mr-1.5" /> Save
        </Button>
      </div>
    </div>
  );
}

export default function ServiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [svc, setSvc] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selected, setSelected] = useState("standard");
  const [addonIdx, setAddonIdx] = useState([]);

  useEffect(() => {
    api.get(`/services/${id}`).then(({ data }) => setSvc(data));
    api.get(`/services/${id}/reviews`).then(({ data }) => setReviews(data));
  }, [id]);

  if (!svc) return <div className="max-w-7xl mx-auto px-6 py-20 text-gray-500">Loading…</div>;

  const pkg = svc.packages.find((p) => p.name === selected) || svc.packages[0];
  const isSingle = svc.package_mode === "single" || svc.packages.length === 1;
  // In single mode keep `selected` aligned with the one available package so
  // re-renders never accidentally fall back to a non-existent tab.
  const activeName = isSingle ? svc.packages[0]?.name : selected;
  const addonTotal = addonIdx.reduce((sum, i) => sum + (svc.addons[i]?.price || 0), 0);
  const grand = pkg.price + addonTotal;

  const buy = async () => {
    if (!user) { nav("/login?redirect=" + encodeURIComponent(`/service/${id}`)); return; }
    try {
      // 1) Create the order
      const { data: order } = await api.post("/orders", {
        service_id: svc.id, package_name: pkg.name, addon_indices: addonIdx,
      });
      // 2) Create Stripe checkout session
      const { data: session } = await api.post(
        `/payments/checkout/order/${order.id}`,
        null,
        { headers: { Origin: window.location.origin } }
      );
      // 3) Redirect to Stripe
      window.location.href = session.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async () => {
    if (!user) { nav("/login"); return; }
    try { await api.post(`/services/${id}/save`); toast.success("Saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const contact = async () => {
    if (!user) { nav("/login"); return; }
    try {
      const { data } = await api.post(`/messages/dm/start?recipient_id=${svc.seller_id}`);
      nav(`/messages?thread=${encodeURIComponent(data.thread_id)}&to=${svc.seller_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const risk = svc.seo_risk ? riskInfo[svc.seo_risk] : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 pb-24 lg:pb-10">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-8 min-w-0">
          <div className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-2">
            <Link to={`/browse?category=${svc.category_slug}`} className="hover:text-mv-primary">{svc.category_slug.replace(/-/g, " ")}</Link>
          </div>
          <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl text-mv-secondary leading-tight" data-testid="service-title">{svc.title}</h1>

          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <Link to={`/seller/${svc.seller_id}`} className="flex items-center gap-3 group min-w-0">
              <Avatar src={svc.seller?.avatar_url} name={svc.seller?.name || ""} size={40} className="shrink-0" testId="seller-avatar" />
              <div className="min-w-0">
                <div className="font-bold text-mv-secondary group-hover:text-mv-primary truncate">{svc.seller?.name}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                  <ShieldCheck size={12} weight="duotone" className="text-mv-primary" />
                  <span className="capitalize">{svc.seller?.tier}</span>
                  {svc.seller?.country && <><span>·</span><span className="inline-flex items-center gap-1"><MapPin size={12} /> {svc.seller.country}</span></>}
                </div>
              </div>
            </Link>
            <div className="sm:ml-auto flex items-center gap-3 text-sm flex-wrap">
              <span className="flex items-center gap-1"><Star size={16} weight="fill" className="text-amber-500" /> <b>{svc.avg_rating?.toFixed(1)}</b> <span className="text-gray-500">({svc.review_count})</span></span>
              {(svc.seller?.trust_score != null || svc.seller?.is_new_seller) && (
                <TrustScore
                  score={svc.seller.trust_score}
                  label={svc.seller.trust_label}
                  isNew={svc.seller.is_new_seller}
                  size="md"
                  testId="detail-trust-score"
                />
              )}
            </div>
          </div>

          <div className="mt-6 rounded-2xl overflow-hidden bg-gray-100 aspect-[16/9] w-full">
            <img src={svc.images?.[0]} alt={svc.title} className="w-full h-full object-cover" />
          </div>

          {risk && (
            <div className={`mt-6 rounded-xl p-4 flex items-start gap-3 ${risk.cls}`} data-testid={`risk-disclosure-${svc.seo_risk}`}>
              <div className="text-xs font-black uppercase tracking-[0.2em] whitespace-nowrap">{risk.label}</div>
              <div className="text-sm">{risk.desc}</div>
            </div>
          )}

          <div className="mt-10">
            <h2 className="font-display font-bold text-2xl text-mv-secondary mb-4">About this service</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{svc.description}</p>
          </div>

          {(() => {
            // Hide each tab when the seller didn't supply content for it.
            // Keep "What is included" always-visible since it's the core
            // pitch surface, even if empty (so the layout has a primary tab).
            const showIncluded = true;
            const showNotIncluded = Array.isArray(svc.not_included) && svc.not_included.length > 0;
            const showRequired = !!(svc.required_info && String(svc.required_info).trim());
            const showOutcome = !!(svc.expected_outcome && String(svc.expected_outcome).trim());
            const anyTab = showIncluded || showNotIncluded || showRequired || showOutcome;
            if (!anyTab) return null;
            return (
              <Tabs defaultValue="included" className="mt-10">
                <TabsList className="bg-gray-100 flex w-full overflow-x-auto no-scrollbar justify-start gap-1">
                  {showIncluded && <TabsTrigger value="included" className="whitespace-nowrap" data-testid="tab-included">What is included</TabsTrigger>}
                  {showNotIncluded && <TabsTrigger value="not-included" className="whitespace-nowrap" data-testid="tab-not-included">Not included</TabsTrigger>}
                  {showRequired && <TabsTrigger value="requirements" className="whitespace-nowrap" data-testid="tab-required-info">Required info</TabsTrigger>}
                  {showOutcome && <TabsTrigger value="outcome" className="whitespace-nowrap" data-testid="tab-outcome">Expected outcome</TabsTrigger>}
                </TabsList>
                {showIncluded && (
                  <TabsContent value="included" className="pt-4">
                    {svc.included?.length ? (
                      <ul className="space-y-2">{svc.included.map((x, i) => <li key={i} className="flex gap-2 text-gray-700"><CheckCircle weight="duotone" className="text-mv-primary mt-0.5" /> {x}</li>)}</ul>
                    ) : (
                      <p className="text-sm text-gray-500">The seller hasn&apos;t listed inclusions for this service yet.</p>
                    )}
                  </TabsContent>
                )}
                {showNotIncluded && (
                  <TabsContent value="not-included" className="pt-4">
                    <ul className="space-y-2">{svc.not_included.map((x, i) => <li key={i} className="flex gap-2 text-gray-700"><X className="text-rose-500 mt-0.5" /> {x}</li>)}</ul>
                  </TabsContent>
                )}
                {showRequired && (
                  <TabsContent value="requirements" className="pt-4 text-gray-700 whitespace-pre-wrap">{svc.required_info}</TabsContent>
                )}
                {showOutcome && (
                  <TabsContent value="outcome" className="pt-4 text-gray-700 whitespace-pre-wrap">{svc.expected_outcome}</TabsContent>
                )}
              </Tabs>
            );
          })()}

          {svc.faqs?.length > 0 && (
            <div className="mt-10" data-testid="faqs-section">
              <h2 className="font-display font-bold text-2xl text-mv-secondary mb-4">FAQs</h2>
              <Accordion type="single" collapsible className="w-full">
                {svc.faqs.map((f, i) => (
                  <AccordionItem value={`f-${i}`} key={i} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                    <AccordionContent>{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          <div className="mt-12">
            <h2 className="font-display font-bold text-2xl text-mv-secondary mb-6">
              Reviews <span className="text-gray-400 font-normal text-base">({reviews.length})</span>
            </h2>
            <div className="space-y-4">
              {reviews.slice(0, 10).map((r) => (
                <div key={r.id} className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Avatar src={r.buyer_avatar} name={r.buyer_name} size={40} testId={`review-avatar-${r.id}`} />
                    <div>
                      <div className="font-bold text-mv-secondary">{r.buyer_name}</div>
                      <div className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-1 text-amber-500">
                      {Array.from({ length: r.overall }).map((_, i) => <Star key={i} size={14} weight="fill" />)}
                    </div>
                  </div>
                  <p className="mt-3 text-gray-700 text-sm">{r.body}</p>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-2 text-xs text-gray-500">
                    <div>Communication: <b className="text-mv-secondary">{r.communication}/5</b></div>
                    <div>Quality: <b className="text-mv-secondary">{r.quality}/5</b></div>
                    <div>Delivery: <b className="text-mv-secondary">{r.delivery}/5</b></div>
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <div className="text-gray-500 text-sm">No reviews yet.</div>}
            </div>
          </div>
        </div>

        {/* SIDEBAR — packages */}
        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-4">
            {isSingle ? (
              <PackageCard
                p={svc.packages[0]} svc={svc} addonIdx={addonIdx}
                setAddonIdx={setAddonIdx} grand={grand}
                onBuy={buy} onContact={contact} onSave={save}
                testIdSuffix="single"
              />
            ) : (
              <Tabs value={activeName} onValueChange={setSelected}>
                <TabsList className="grid grid-cols-3 w-full">
                  {svc.packages.map((p) => <TabsTrigger key={p.name} value={p.name} data-testid={`pkg-tab-${p.name}`}>{p.title}</TabsTrigger>)}
                </TabsList>
                {svc.packages.map((p) => (
                  <TabsContent value={p.name} key={p.name}>
                    <PackageCard
                      p={p} svc={svc} addonIdx={addonIdx}
                      setAddonIdx={setAddonIdx} grand={grand}
                      onBuy={buy} onContact={contact} onSave={save}
                      testIdSuffix={p.name}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
        </aside>
      </div>

      {/* Sticky mobile bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 lg:hidden bg-white border-t border-gray-200 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.12)] px-4 py-3 flex items-center gap-3 safe-area-bottom"
           data-testid="mobile-sticky-cta">
        <div className="min-w-0">
          <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{pkg.title}</div>
          <div className="font-display font-black text-mv-secondary text-lg leading-tight">${grand}</div>
        </div>
        <Button onClick={buy} data-testid="buy-now-btn-mobile"
          className="ml-auto h-11 bg-mv-primary hover:bg-mv-primary-light text-white font-black rounded-xl px-5">
          Continue
        </Button>
      </div>
    </div>
  );
}
